/* eslint-disable prettier/prettier */
import dataService from 'services/dataService';
import {
  apiDoctorOnboardingStatus,
  apiDoctorPatients,
  ONBOARDING_CSV_REPORT,
} from 'services/dataService/resources';
import { generateFullName } from 'utils/badgeHelpers';

const getCountByStatus = (patientsByStatus, statusFind) => {
  if (statusFind === 'patient_refused') {
    return (
      patientsByStatus
        .filter((el) => el.status.includes('patient_refused'))
        .reduce((acum, current) => acum + parseInt(current.count), 0) || 0
    );
  }
  const obj = patientsByStatus.find(({ status }) => status === statusFind);
  return obj ? obj.count : 0;
};

const toLocalStatus = (status) => {
  switch (status) {
    case 'patient_referral':
      return 'referral';
    case 'referral_withdrawn':
      return 'referral_withdrawn';
    case 'patient_notified':
      return 'notified';
    case 'calling_queue':
      return 'queue';
    case 'patient_no_contact':
      return 'noContact';
    case 'patient_no_contact_day_20':
      return 'noContact_20days';
    case 'patient_no_contact_day_30':
      return 'noContact_30days';
    case 'patient_no_contact_closed':
      return 'noContact_closed';
    case 'patient_refused':
      return 'refused';
    case 'patient_refused_connectivity_or_tech_barier':
      return 'patient_refused_connectivity_or_tech_barier';
    case 'patient_refused_copay_insurance':
      return 'patient_refused_copay_insurance';
    case 'patient_refused_not_interested_not_necessary_suspect_scam':
      return 'patient_refused_not_interested_not_necessary_suspect_scam';
    case 'patient_refused_expired':
      return 'patient_refused_expired';
    case 'patient_refused_there_is_no_one_to_help':
      return 'patient_refused_there_is_no_one_to_help';
    case 'patient_refused_has_a_new_physician':
      return 'patient_refused_has_a_new_physician';
    case 'patient_refused_wants_to_discuss_with_physician':
      return 'patient_refused_wants_to_discuss_with_physician';
    case 'patient_refused_wrong_contact_information':
      return 'patient_refused_wrong_contact_information';
    case 'patient_refused_no_contact':
      return 'patient_refused_no_contact';
    case 'insurance_refused':
      return 'insuranceRefused';
    case 'onboarding':
      return 'onboarding';
    case 'onboarding_unsuccessful':
      return 'onboarding_unsuccessful';
    default:
      return '';
  }
};

const fromLocalStatus = (status) => {
  switch (status) {
    case 'referral':
      return 'patient_referral';
    case 'referral_withdrawn':
      return 'referral_withdrawn';
    case 'notified':
      return 'patient_notified';
    case 'queue':
      return 'calling_queue';
    case 'noContact':
      return 'patient_no_contact';
    case 'noContact_20days':
      return 'patient_no_contact_day_20';
    case 'noContact_30days':
      return 'patient_no_contact_day_30';
    case 'noContact_closed':
      return 'patient_no_contact_closed';
    case 'refused':
      return 'patient_refused';
    case 'patient_refused_connectivity_or_tech_barier':
      return 'patient_refused_connectivity_or_tech_barier';
    case 'patient_refused_copay_insurance':
      return 'patient_refused_copay_insurance';
    case 'patient_refused_not_interested_not_necessary_suspect_scam':
      return 'patient_refused_not_interested_not_necessary_suspect_scam';
    case 'patient_refused_expired':
      return 'patient_refused_expired';
    case 'patient_refused_there_is_no_one_to_help':
      return 'patient_refused_there_is_no_one_to_help';
    case 'patient_refused_has_a_new_physician':
      return 'patient_refused_has_a_new_physician';
    case 'patient_refused_wants_to_discuss_with_physician':
      return 'patient_refused_wants_to_discuss_with_physician';
    case 'patient_refused_wrong_contact_information':
      return 'patient_refused_wrong_contact_information';
    case 'patient_refused_no_contact':
      return 'patient_refused_no_contact';
    case 'insuranceRefused':
      return 'insurance_refused';
    case 'onboarding':
      return 'onboarding';
    case 'onboarding_unsuccessful':
      return 'onboarding_unsuccessful';
    default:
      return '';
  }
};

const INITIAL_STATE = {
  statuses: {
    referral: 0,
    referral_withdrawn: 0,
    queue: 0,
    noContact: 0,
    refused: 0,
    insuranceRefused: 0,
    onboarding: 0,
    onboarding_unsuccessful: 0,
    total: 0,
    active: 0,
  },
  insurances: [],
  patients: [],
  patientCount: null,
  clinic: null,
  doctor: null,
  patientGuid: null,
};

const onboardingStatus = {
  state: INITIAL_STATE,
  reducers: {
    resetState: () => INITIAL_STATE,
    setData: (
      state,
      { patientsByStatus, patientsByInsurances, activePatients, totalPatients },
    ) => ({
      ...state,
      statuses: {
        referral: getCountByStatus(patientsByStatus, 'patient_referral'),
        referral_withdrawn: getCountByStatus(
          patientsByStatus,
          'referral_withdrawn',
        ),
        queue: getCountByStatus(patientsByStatus, 'calling_queue'),
        noContact: getCountByStatus(patientsByStatus, 'patient_no_contact'),
        refused: getCountByStatus(patientsByStatus, 'patient_refused'),
        insuranceRefused: getCountByStatus(
          patientsByStatus,
          'insurance_refused',
        ),
        onboarding: getCountByStatus(patientsByStatus, 'onboarding'),
        onboarding_unsuccessful: getCountByStatus(
          patientsByStatus,
          'onboarding_unsuccessful',
        ),
        total: totalPatients,
        active: activePatients,
      },
      insurances: patientsByInsurances,
    }),
    setPatientData: (state, { items, count }) => ({
      ...state,
      patients: items.map((item) => ({
        guid: item.guid,
        name: `${item.firstName} ${item.lastName}`,
        mrn: item.mrn,
        dob: item.birthDate,
        phone: item.phone,
        insType: item.insurances.length ? item.insurances[0].type : null,
        clinic: item.clinic,
        primaryDoctor:
          item.primaryDoctor &&
          generateFullName(
            item.primaryDoctor.title,
            item.primaryDoctor.firstName,
            null,
            item.primaryDoctor.lastName,
          ),
        insProvider: item.insurances.length
          ? item.insurances.map((insurance) => insurance.provider).join(',\n')
          : null,
        insNumber: item.insurances.length
          ? item.insurances.map((insurance) => insurance.number).join(',\n')
          : null,
        fulfillment: toLocalStatus(item.status),
        practiceEmail: item.primaryDoctor.email,
        createdAt: item?.createdAt,
      })),
      patientCount: count,
    }),
    setPatientGuid: (state, { patientGuid }) => ({
      ...state,
      patientGuid,
    }),
    setClinic: (state, payload) => ({
      ...state,
      clinic: payload,
    }),
    setDoctor: (state, payload) => ({
      ...state,
      doctor: payload,
    }),
    setStatus: (state, payload) => ({
      ...state,
      status: payload,
      patientStatus: fromLocalStatus(payload),
    }),
    setReferralDateSort: (state, payload) => ({
      ...state,
      referralDateSort: payload,
    }),
  },
  effects: (dispatch) => ({
    async getOnboardingStatusReport(payload, state) {
      const { clinic, doctor } = state.onboardingStatus;
      const { data } = await dataService.getList(
        apiDoctorOnboardingStatus({ clinicGuid: clinic, doctorGuid: doctor }),
      );

      if (data) {
        dispatch.onboardingStatus.setData(data);
      }
    },

    async getPatientsReport(payload, state) {
      const { clinic, doctor, status, referralDateSort } =
        state.onboardingStatus;
      const { data } = await dataService.getList(
        apiDoctorPatients({
          clinicGuid: clinic,
          status: fromLocalStatus(status) || 'only_onboarding_flow',
          patientGuid: payload?.patient,
          doctorGuid: doctor,
          referralDateSort,
          clinicsStatus: 'active,delinquent',
          page: payload?.page || 1,
          items: 10,
        }),
      );

      if (data) {
        dispatch.onboardingStatus.setPatientData(data);
      }
    },

    async setClinicOnboardingStatus(clinic) {
      dispatch.onboardingStatus.setClinic(clinic);
      dispatch.onboardingStatus.getOnboardingStatusReport();
      dispatch.onboardingStatus.getPatientsReport();
    },
    async setDoctorOnboardingStatus(doctor) {
      dispatch.onboardingStatus.setDoctor(doctor);
      dispatch.onboardingStatus.getOnboardingStatusReport();
      dispatch.onboardingStatus.getPatientsReport();
    },
    async setPatient(patient) {
      dispatch.onboardingStatus.setPatientGuid({ patientGuid: patient });
      dispatch.onboardingStatus.getPatientsReport({ patient });
    },

    async setStatus(status) {
      dispatch.onboardingStatus.getPatientsReport({
        status: fromLocalStatus(status),
      });
    },
    async getOnboardingCsvReport(payload, state) {
      const { clinic, status, doctor, patientGuid } = state.onboardingStatus;
      const body = {
        status: fromLocalStatus(status) || 'only_onboarding_flow',
      };
      if (clinic) body.clinicGuid = clinic;
      if (doctor) body.doctorGuid = doctor;
      if (patientGuid) body.patientGuid = patientGuid;
      const { error, data } = await dataService.createOne(
        ONBOARDING_CSV_REPORT,
        body,
      );
      return { error, data };
    },
    resetFilters() {
      dispatch.onboardingStatus.resetState();
    },
  }),
};

export default onboardingStatus;
